import { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import {
  employeeConfirmationService,
  employeeSeparationService,
  employeeTransferService,
} from "../../services/api";

function mapStatus(raw) {
  const s = String(raw || "").toLowerCase();
  if (s === "pending_approval" || s === "draft" || s === "on_hold" || s === "extended") {
    return "Pending";
  }
  if (s === "rejected" || s === "cancelled") return "Rejected";
  if (
    s === "approved" ||
    s === "applied" ||
    s === "completed" ||
    s === "confirmed" ||
    s === "clearance_pending" ||
    s === "fnf_pending"
  ) {
    return "Approved";
  }
  return raw || "Pending";
}

/**
 * Shared detail / approve-reject modal for Confirmation, Transfer and Separation insights.
 */
export default function LifecycleInsightDetailModal({
  darkMode = false,
  row,
  kind, // "confirmation" | "transfer" | "separation"
  onClose,
  onUpdated,
}) {
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [detail, setDetail] = useState(null);
  const [comments, setComments] = useState("");

  const requestId =
    kind === "confirmation"
      ? row?.confirmationRequestId
      : kind === "transfer"
        ? row?.transferRequestId
        : row?.separationRequestId;
  const service =
    kind === "confirmation"
      ? employeeConfirmationService
      : kind === "transfer"
        ? employeeTransferService
        : employeeSeparationService;
  const title =
    kind === "confirmation"
      ? "Confirmation"
      : kind === "transfer"
        ? "Transfer"
        : "Separation / Resignation";

  const load = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    try {
      const res = await service.getRequest(requestId);
      setDetail(res?.data || null);
    } catch (error) {
      toast.error(error?.response?.data?.message || `Failed to load ${title.toLowerCase()} request`);
    } finally {
      setLoading(false);
    }
  }, [requestId, service, title]);

  useEffect(() => {
    load();
  }, [load]);

  const status = detail?.status || row?.status;
  const canAct = ["pending_approval", "on_hold", "extended"].includes(
    String(status || "").toLowerCase()
  );
  const approvals = Array.isArray(detail?.approvals) ? detail.approvals : [];
  const checklist =
    Array.isArray(detail?.checklistItems) ? detail.checklistItems : [];

  const runAction = async (action) => {
    const note = String(comments || "").trim();
    if (!note) {
      toast.error("Remarks are required");
      return;
    }
    if (!requestId) return;
    setActing(true);
    try {
      if (action === "approve") {
        await service.approve(requestId, { comments: note, remarks: note });
        toast.success(`${title} step approved`);
      } else {
        await service.reject(requestId, { comments: note, remarks: note });
        toast.success(`${title} rejected`);
      }
      setComments("");
      await load();
      onUpdated?.();
    } catch (error) {
      toast.error(error?.response?.data?.message || `Failed to ${action}`);
    } finally {
      setActing(false);
    }
  };

  const completeChecklist = async (itemId) => {
    const note =
      window.prompt("Remarks for completing this checklist task:") || "";
    if (!String(note).trim()) {
      toast.error("Remarks are required");
      return;
    }
    setActing(true);
    try {
      await service.completeChecklistItem(requestId, itemId, {
        remarks: note,
        comments: note,
      });
      toast.success("Checklist task completed");
      await load();
      onUpdated?.();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to complete checklist task");
    } finally {
      setActing(false);
    }
  };

  const panelClass = darkMode
    ? "bg-[#1a1a1a] text-gray-100 border-[#3C3C4C]"
    : "bg-white text-gray-900 border-gray-200";
  const inputClass = darkMode
    ? "mt-1 w-full rounded-md border border-[#3C3C4C] bg-[#121212] px-2 py-1.5 text-sm"
    : "mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm";

  return (
    <div className="fixed inset-0 z-[10020] flex items-center justify-center bg-black/45 p-4">
      <div
        className={`flex max-h-[85vh] w-full max-w-xl flex-col overflow-hidden rounded-xl border shadow-xl ${panelClass}`}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-[#3C3C4C]">
          <div>
            <div className="text-sm font-semibold">{title} request</div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400">
              {row?.employeeName || detail?.employee?.user?.name || "Employee"}
              {row?.employeeCode || detail?.employee?.empCode
                ? ` (${row?.employeeCode || detail?.employee?.empCode})`
                : ""}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-[#2a2a2a]"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3 text-[12px]">
          {loading ? (
            <div className="py-8 text-center text-gray-500">Loading…</div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] uppercase text-gray-500">Status</div>
                  <div className="font-semibold">{mapStatus(status)}</div>
                  <div className="text-[10px] text-gray-500">{status}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-gray-500">Pending with</div>
                  <div className="font-semibold">
                    {detail?.pendingLabel || row?.pendingWith || "—"}
                  </div>
                </div>
                {kind === "transfer" ? (
                  <>
                    <div>
                      <div className="text-[10px] uppercase text-gray-500">Effective date</div>
                      <div className="font-medium">{detail?.effectiveDate || "—"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-gray-500">Transfer type</div>
                      <div className="font-medium">{detail?.transferType || "—"}</div>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <div className="text-[10px] uppercase text-gray-500">Separation date</div>
                      <div className="font-medium">{detail?.separationDate || "—"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-gray-500">Last working day</div>
                      <div className="font-medium">
                        {detail?.lastWorkingDay || detail?.relievingDate || "—"}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {approvals.length > 0 ? (
                <div>
                  <div className="mb-1 text-[10px] font-semibold uppercase text-gray-500">
                    Workflow
                  </div>
                  <div className="space-y-1">
                    {approvals.map((a) => (
                      <div
                        key={a.id || `${a.stepOrder}-${a.role}`}
                        className={`rounded border px-2 py-1 ${
                          darkMode ? "border-[#3C3C4C]" : "border-gray-200"
                        }`}
                      >
                        Step {a.stepOrder}: {a.role} — {a.action || "pending"}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {checklist.length > 0 ? (
                <div>
                  <div className="mb-1 text-[10px] font-semibold uppercase text-gray-500">
                    Checklist
                  </div>
                  <div className="space-y-1">
                    {checklist.map((item) => (
                      <div
                        key={item.id || item.itemKey}
                        className={`flex items-center justify-between gap-2 rounded border px-2 py-1 ${
                          darkMode ? "border-[#3C3C4C]" : "border-gray-200"
                        }`}
                      >
                        <span>
                          {item.itemName || item.name}
                          {item.mandatory ? " *" : ""}
                        </span>
                        {item.status === "pending" ? (
                          <button
                            type="button"
                            disabled={acting}
                            onClick={() => completeChecklist(item.id)}
                            className="rounded border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-800"
                          >
                            Done
                          </button>
                        ) : (
                          <span className="text-[10px] text-gray-500">{item.status}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {canAct ? (
                <label className="block">
                  Remarks (required for approve / reject)
                  <textarea
                    rows={2}
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    className={inputClass}
                    placeholder="Enter remarks…"
                  />
                </label>
              ) : null}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3 dark:border-[#3C3C4C]">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold dark:border-[#3C3C4C]"
          >
            Close
          </button>
          {canAct ? (
            <>
              <button
                type="button"
                disabled={acting || loading}
                onClick={() => runAction("reject")}
                className="rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-800"
              >
                Reject
              </button>
              <button
                type="button"
                disabled={acting || loading}
                onClick={() => runAction("approve")}
                className="rounded-md px-3 py-1.5 text-xs font-semibold rh-btn-primary"
              >
                Approve
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
